import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/legal.css';

const CONTACT_EMAIL = 'hello@covo.app';
const APP_NAME = 'Covo';
const EFFECTIVE_DATE = 'May 6, 2025';

export default function Terms() {
  const navigate = useNavigate();
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <span className="legal-logo" onClick={() => navigate('/')}>COVO</span>
        <button className="legal-back" onClick={() => navigate('/')}>← Back</button>
      </nav>

      <div className="legal-body">
        <h1>Terms of Service</h1>
        <p className="legal-date">Effective date: {EFFECTIVE_DATE}</p>

        <p>
          Welcome to {APP_NAME}. By creating an account or using the service, you agree to these
          Terms of Service ("Terms"). Please read them carefully.
        </p>

        <h2>1. Who can use Covo</h2>
        <p>
          You must be at least 13 years old to use Covo. By using the app you confirm you meet this
          requirement. If you are using Covo on behalf of an organization, you represent that you
          have authority to bind that organization to these Terms.
        </p>

        <h2>2. Your account</h2>
        <ul>
          <li>You are responsible for keeping your password secure and for all activity under your account.</li>
          <li>You must provide accurate information when creating your account.</li>
          <li>You may not share your account with others or create accounts on behalf of someone else without their permission.</li>
          <li>You may not use Covo if you have been previously banned from the service.</li>
        </ul>

        <h2>3. What you can do on Covo</h2>
        <p>Covo lets you:</p>
        <ul>
          <li>Create and manage social activities and plans</li>
          <li>Invite friends to activities and RSVP to others' plans</li>
          <li>Message friends and activity participants</li>
          <li>Connect your external calendars to view your schedule</li>
          <li>Organize friends into groups and a Close Circle for availability sharing</li>
        </ul>

        <h2>4. What you may not do</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Post content that is illegal, harassing, threatening, defamatory, or discriminatory</li>
          <li>Impersonate another person or misrepresent your identity</li>
          <li>Send spam or unsolicited messages to other users</li>
          <li>Attempt to access another user's account without authorization</li>
          <li>Scrape, crawl, or use automated means to extract data from the service</li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of the app</li>
          <li>Use the service to violate any applicable law or regulation</li>
          <li>Interfere with or disrupt the integrity or performance of the service</li>
        </ul>

        <h2>5. Your content</h2>
        <p>
          You retain ownership of the content you post (activity descriptions, messages, profile
          information). By posting content on Covo, you grant us a non-exclusive, worldwide,
          royalty-free license to store, display, and distribute your content solely for the purpose
          of operating the service.
        </p>
        <p>
          You are solely responsible for your content. We do not review content before it is posted
          and cannot be held liable for content created by users.
        </p>

        <h2>6. Our content and intellectual property</h2>
        <p>
          The Covo name, logo, and app interface are our property. You may not copy, reproduce, or
          use them without our written permission. Nothing in these Terms transfers ownership of our
          intellectual property to you.
        </p>

        <h2>7. Termination</h2>
        <p>
          You may stop using Covo at any time and request account deletion by emailing{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <p>
          We may suspend or terminate your account if you violate these Terms, engage in harmful
          behavior, or if required by law. We will give reasonable notice where possible.
        </p>

        <h2>8. Disclaimers</h2>
        <p>
          Covo is provided "as is" without warranties of any kind. We do not guarantee that the
          service will be available at all times, error-free, or secure. Use the service at your
          own risk.
        </p>
        <p>
          We are not responsible for the actions or content of other users. Any plans or activities
          coordinated through Covo are between the participating users.
        </p>

        <h2>9. Limitation of liability</h2>
        <p>
          To the fullest extent permitted by law, Covo and its operators shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages arising from your
          use of or inability to use the service — even if we have been advised of the possibility
          of such damages.
        </p>
        <p>
          Our total liability to you for any claim arising from these Terms or your use of the
          service shall not exceed the amount you paid us in the 12 months preceding the claim
          (or $10 if you have not paid anything).
        </p>

        <h2>10. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes by
          updating the effective date above or by sending a notice to your registered email.
          Continued use of Covo after changes means you accept the updated Terms.
        </p>

        <h2>11. Governing law</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of France, without
          regard to conflict of law principles. Any disputes shall be resolved in the courts of Paris.
        </p>

        <h2>12. Contact</h2>
        <p>
          Questions about these Terms? Email us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </div>

      <footer className="legal-footer">
        <span className="legal-footer-logo">COVO</span>
        <div className="legal-footer-links">
          <button onClick={() => navigate('/privacy')}>Privacy Policy</button>
          <button onClick={() => navigate('/terms')}>Terms of Service</button>
        </div>
      </footer>
    </div>
  );
}
